// 1.3.74: MVP 付费功能 - 计费相关类型定义

/**
 * 订阅状态
 */
export type SubscriptionStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'canceled';

/**
 * 订阅信息
 */
export interface SubscriptionInfo {
  success: boolean;
  message?: string;
  organization_id?: string;
  name?: string;
  org_type?: 'personal' | 'business';
  plan_type?: 'free' | 'individual' | 'standard';
  trial_ends_at?: string;
  trial_days_remaining?: number;
  subscription_status?: SubscriptionStatus;
  effective_subscription_status?: SubscriptionStatus; // 1.6.57: 有效订阅状态（优先使用覆盖）
  subscription_current_period_start?: string;
  subscription_current_period_end?: string;
  subscription_cancel_at_period_end?: boolean;
  has_stripe_customer?: boolean;
  has_active_subscription?: boolean;
  max_members?: number;
  max_storage_gb?: number;
  public_link_quota?: number;
  purchased_seats?: number; // 1.3.79: Standard 套餐实际购买的席位数
  // 1.3.97: 使用量统计
  storage_used_gb?: number;
  public_link_used?: number;
  // 1.6.57: 管理员覆盖相关字段
  is_internal?: boolean;
  admin_override_subscription_status?: SubscriptionStatus | null;
  admin_override_until?: string | null;
}

/**
 * Checkout Session 创建参数
 */
export interface CreateCheckoutParams {
  organizationId: string;
  plan: 'individual' | 'standard';
  quantity?: number;  // Standard 套餐的人数
}

/**
 * Checkout Session 响应
 */
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

/**
 * Portal Session 响应
 */
export interface PortalSessionResponse {
  url: string;
}

/**
 * 套餐显示配置
 * 1.3.80: 使用 i18n 翻译键支持多语言
 */
export interface PlanDisplayConfig {
  id: 'free' | 'individual' | 'standard';
  nameKey: string;
  descriptionKey: string;
  descriptionFallback: string;
  price: number;
  priceUnitKey: string;
  priceUnitFallback: string;
  featureKeys: string[];
  featureFallbacks: string[];
  recommended?: boolean;
  disabled?: boolean;
}

/**
 * 套餐配置常量
 * 1.3.80: 使用 i18n 翻译键支持多语言
 */
export const PLAN_DISPLAY_CONFIGS: PlanDisplayConfig[] = [
  {
    id: 'free',
    nameKey: 'planFree',
    descriptionKey: 'planFreeDesc',
    descriptionFallback: '免费试用，体验核心功能',
    price: 0,
    priceUnitKey: 'perMonth',
    priceUnitFallback: '/月',
    featureKeys: [
      'planFeatureTrial7Days',
      'planFeatureBasicChat',
      'planFeature1KB',
      'planFeatureNoPublicLink',
    ],
    featureFallbacks: [
      '7天免费试用',
      '基础对话功能',
      '1个知识库',
      '无公开链接',
    ],
    disabled: true,
  },
  {
    id: 'individual',
    nameKey: 'planIndividual',
    descriptionKey: 'planIndividualDesc',
    descriptionFallback: '适合个人用户或自由职业者',
    price: 2500,
    priceUnitKey: 'perMonth',
    priceUnitFallback: '/月',
    featureKeys: [
      'planFeature1Person',
      'planFeature1GBStorage',
      'planFeature5000PublicLinks',
      'planFeatureUnlimitedChat',
      'planFeatureAllBasicFeatures',
    ],
    featureFallbacks: [
      '1人使用',
      '1GB 存储空间',
      '5,000次/月 公开链接访问',
      '无限内部对话',
      '所有基础功能',
    ],
  },
  {
    id: 'standard',
    nameKey: 'planStandard',
    descriptionKey: 'planStandardDesc',
    descriptionFallback: '适合小型团队协作',
    price: 2300,
    priceUnitKey: 'perPersonPerMonth',
    priceUnitFallback: '/人/月',
    featureKeys: [
      'planFeature2to50People',
      'planFeature10GBStorage',
      'planFeature10000PublicLinks',
      'planFeatureUnlimitedChat',
      'planFeatureSharedKB',
      'planFeatureMemberManagement',
    ],
    featureFallbacks: [
      '2-50人团队',
      '10GB 存储空间',
      '10,000次/月 公开链接访问',
      '无限内部对话',
      '共享知识库',
      '成员管理',
    ],
    recommended: true,
  },
];

/**
 * 订阅状态显示配置
 * 1.3.80: 使用 i18n 翻译键支持多语言
 */
export const SUBSCRIPTION_STATUS_CONFIG: Record<SubscriptionStatus, {
  labelKey: string;
  labelFallback: string;
  color: string;
  bgColor: string;
}> = {
  none: {
    labelKey: 'subscriptionStatusNone',
    labelFallback: '未订阅',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
  trialing: {
    labelKey: 'subscriptionStatusTrialing',
    labelFallback: '试用中',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  active: {
    labelKey: 'subscriptionStatusActive',
    labelFallback: '已订阅',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  past_due: {
    labelKey: 'subscriptionStatusPastDue',
    labelFallback: '待支付',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  canceled: {
    labelKey: 'subscriptionStatusCanceled',
    labelFallback: '已取消',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
};
